import type { ReactNode, ButtonHTMLAttributes, InputHTMLAttributes, SelectHTMLAttributes } from "react";
import { useEffect, useId, useState } from "react";

/**
 * The interface's primitives.
 *
 * Everything here is deliberately flat and ruled rather than boxed and shadowed: the
 * page should read like a ledger, where structure comes from hairlines and typography
 * instead of from cards floating above a grey background.
 */

/* -------------------------------------------------------------------------- */
/* Buttons                                                                     */
/* -------------------------------------------------------------------------- */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  loading?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-verdigris text-paper border-verdigris hover:bg-verdigris-bright hover:border-verdigris-bright",
  secondary:
    "bg-paper-raised text-ink border-rule-strong hover:bg-paper-sunk",
  ghost:
    "bg-transparent text-ink-muted border-transparent hover:text-ink hover:bg-paper-sunk",
  danger:
    "bg-sienna text-white border-sienna hover:brightness-110",
};

export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  disabled,
  children,
  className = "",
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-md border font-medium",
        "transition-[background-color,border-color,color,opacity] duration-150",
        "disabled:opacity-45 disabled:cursor-not-allowed",
        size === "sm" ? "px-2.5 py-1 text-[12px]" : "px-3.5 py-2 text-[13px]",
        BUTTON_VARIANTS[variant],
        className,
      ].join(" ")}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <svg className="h-3 w-3 animate-spin" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M8 1.5a6.5 6.5 0 0 1 6.5 6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Status pills                                                                */
/* -------------------------------------------------------------------------- */

type Tone = "success" | "pending" | "failure" | "neutral" | "info" | "warning";

const TONE_STYLES: Record<Tone, string> = {
  success: "bg-verdigris-wash text-verdigris ring-verdigris/20",
  pending: "bg-indigo-wash text-indigo-ink ring-indigo-ink/20",
  info: "bg-indigo-wash text-indigo-ink ring-indigo-ink/20",
  failure: "bg-sienna-wash text-sienna ring-sienna/20",
  warning: "bg-brass-wash text-brass ring-brass/25",
  neutral: "bg-paper-sunk text-ink-muted ring-rule-strong",
};

/** STATUS_TONES maps every lifecycle state the platform can report onto a tone, so a
 *  status reads the same way wherever it appears. */
const STATUS_TONES: Record<string, Tone> = {
  // payments
  succeeded: "success",
  paid: "success",
  delivered: "success",
  approved: "success",
  created: "neutral",
  pending: "pending",
  processing: "pending",
  awaiting_payment: "pending",
  awaiting_otp: "pending",
  requires_action: "warning",
  submitted: "pending",
  in_review: "pending",
  kyc_submitted: "pending",
  kyc_in_review: "pending",
  info_requested: "warning",
  pending_kyc: "neutral",
  draft: "neutral",
  failed: "failure",
  cancelled: "failure",
  expired: "failure",
  rejected: "failure",
  suspended: "failure",
  dead: "failure",
  closed: "neutral",
  // fraud rule actions
  block: "failure",
  flag: "warning",
};

export function StatusPill({ status, className = "" }: { status: string; className?: string }) {
  const tone = STATUS_TONES[status] ?? "neutral";
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset whitespace-nowrap",
        TONE_STYLES[tone],
        className,
      ].join(" ")}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />
      {status.replace(/_/g, " ")}
    </span>
  );
}

/** ModePill distinguishes sandbox from live at a glance. Confusing the two is the
 *  most expensive mistake a merchant can make, so it is always visible and always
 *  the same colour. */
export function ModePill({ mode }: { mode: string }) {
  const live = mode === "live";
  return (
    <span
      className={[
        "inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset",
        live
          ? "bg-verdigris text-paper ring-verdigris"
          : "bg-brass-wash text-brass ring-brass/30",
      ].join(" ")}
    >
      {live ? "Live" : "Sandbox"}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Layout                                                                      */
/* -------------------------------------------------------------------------- */

export function Sheet({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`sheet ${className}`}>{children}</div>;
}

export function SheetHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-rule px-5 py-4">
      <div className="min-w-0">
        <h2 className="text-[17px] leading-tight text-ink-strong">{title}</h2>
        {description && <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="settle mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <p className="label mb-1.5">{eyebrow}</p>}
        <h1 className="text-[30px] leading-[1.1] text-ink-strong">{title}</h1>
        {description && (
          <p className="mt-1.5 max-w-2xl text-[13.5px] leading-relaxed text-ink-muted">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Tables                                                                      */
/* -------------------------------------------------------------------------- */

export function Table({ children, className = "" }: { children: ReactNode; className?: string }) {
  // Tables are the only element permitted to scroll horizontally; the page itself
  // must never do so.
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full border-collapse text-[13px]">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = "left",
  className = "",
}: {
  children?: ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
}) {
  return (
    <th
      className={[
        "label border-b border-rule-strong px-4 py-2.5 font-semibold",
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        className,
      ].join(" ")}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = "left",
  className = "",
  mono = false,
}: {
  children?: ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
  mono?: boolean;
}) {
  return (
    <td
      className={[
        "border-b border-rule px-4 py-3 align-middle",
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        mono ? "font-mono text-[12px]" : "",
        className,
      ].join(" ")}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  onClick,
  className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const interactive = Boolean(onClick);
  return (
    <tr
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      className={[
        "transition-colors duration-100",
        interactive ? "cursor-pointer hover:bg-paper-sunk" : "",
        className,
      ].join(" ")}
    >
      {children}
    </tr>
  );
}

/* -------------------------------------------------------------------------- */
/* Feedback                                                                    */
/* -------------------------------------------------------------------------- */

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {/* A ruled page with nothing written on it — the empty ledger. */}
      <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true" className="mb-4 opacity-30">
        <rect x="6.5" y="3.5" width="31" height="37" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M13 14h18M13 21h18M13 28h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <p className="font-display text-[17px] text-ink">{title}</p>
      {description && (
        <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-ink-muted">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorNotice({ error, className = "" }: { error: unknown; className?: string }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : "Something went wrong.";
  const fields = error && typeof error === "object" && "fields" in error
    ? (error as { fields: { field: string; message: string }[] }).fields
    : [];

  return (
    <div
      role="alert"
      className={`rounded-md border border-sienna/25 bg-sienna-wash px-4 py-3 ${className}`}
    >
      <p className="text-[13px] font-medium text-sienna">{message}</p>
      {fields.length > 0 && (
        <ul className="mt-2 space-y-1">
          {fields.map((f) => (
            <li key={f.field} className="text-[12.5px] text-sienna/85">
              <span className="font-mono text-[11.5px]">{f.field}</span> — {f.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Skeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-px p-4" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-9 animate-pulse rounded-sm bg-paper-sunk"
          style={{ animationDelay: `${i * 70}ms` }}
        />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Forms                                                                       */
/* -------------------------------------------------------------------------- */

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function Field({ label, hint, error, className = "", ...props }: FieldProps) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-[12.5px] font-medium text-ink">
        {label}
        {props.required && <span className="ml-0.5 text-sienna">*</span>}
      </label>
      <input
        {...props}
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={hint || error ? `${id}-help` : undefined}
        className={[
          "w-full rounded-md border bg-paper-raised px-3 py-2 text-[13.5px]",
          "placeholder:text-ink-faint transition-colors duration-150",
          "focus:border-verdigris-bright focus:outline-none focus:ring-2 focus:ring-verdigris-bright/15",
          error ? "border-sienna" : "border-rule-strong",
        ].join(" ")}
      />
      {(hint || error) && (
        <p
          id={`${id}-help`}
          className={`mt-1 text-[12px] ${error ? "text-sienna" : "text-ink-muted"}`}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  options: { value: string; label: string }[];
}

export function SelectField({ label, hint, options, className = "", ...props }: SelectFieldProps) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-[12.5px] font-medium text-ink">
        {label}
      </label>
      <select
        {...props}
        id={id}
        className="w-full rounded-md border border-rule-strong bg-paper-raised px-3 py-2 text-[13.5px] focus:border-verdigris-bright focus:outline-none focus:ring-2 focus:ring-verdigris-bright/15"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && <p className="mt-1 text-[12px] text-ink-muted">{hint}</p>}
    </div>
  );
}

interface TextAreaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
}

/** TextAreaField is the multi-line counterpart to Field.
 *
 *  It exists because several screens had a bare <label> beside a <textarea> with no
 *  association between them, which leaves the control unnamed to a screen reader. */
export function TextAreaField({ label, hint, className = "", ...props }: TextAreaFieldProps) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-[12.5px] font-medium text-ink">
        {label}
        {props.required && <span className="ml-0.5 text-sienna">*</span>}
      </label>
      <textarea
        {...props}
        id={id}
        aria-describedby={hint ? `${id}-help` : undefined}
        className="w-full rounded-md border border-rule-strong bg-paper-raised px-3 py-2 text-[13.5px] focus:border-verdigris-bright focus:outline-none focus:ring-2 focus:ring-verdigris-bright/15"
      />
      {hint && (
        <p id={`${id}-help`} className="mt-1 text-[12px] text-ink-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Utilities                                                                   */
/* -------------------------------------------------------------------------- */

/** CopyButton reveals whether the copy succeeded, because a silently failed copy of
 *  an API secret that is shown only once is a genuinely costly failure. */
export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      setState("failed");
    }
  }

  return (
    <Button size="sm" variant="ghost" onClick={copy} aria-live="polite">
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label}
    </Button>
  );
}

/** Mono renders an identifier or reference in the monospace face, with the option to
 *  copy it — support conversations are conducted in these strings. */
export function Mono({
  children,
  copyable,
  className = "",
}: {
  children: string;
  copyable?: boolean;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <code className="font-mono text-[12px] text-ink-muted">{children}</code>
      {copyable && <CopyButton value={children} label="" />}
    </span>
  );
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // Escape must always close a dialog: an operator part-way through a destructive
  // action needs an unambiguous way out.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div
        className="fixed inset-0 bg-ink/45 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="settle relative z-10 my-auto w-full max-w-lg rounded-lg border border-rule-strong bg-paper-raised shadow-lift"
      >
        <div className="border-b border-rule px-5 py-4">
          <h2 className="text-[18px] leading-tight text-ink-strong">{title}</h2>
          {description && <p className="mt-1 text-[13px] text-ink-muted">{description}</p>}
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-rule px-5 py-3.5">{footer}</div>
        )}
      </div>
    </div>
  );
}

/** Pagination is offset-based, matching the API. */
export function Pagination({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}) {
  if (total <= limit) return null;
  const from = offset + 1;
  const to = Math.min(offset + limit, total);

  return (
    <div className="flex items-center justify-between border-t border-rule px-4 py-3">
      <p className="text-[12.5px] text-ink-muted">
        <span className="tabular">{from}</span>–<span className="tabular">{to}</span> of{" "}
        <span className="tabular">{total.toLocaleString()}</span>
      </p>
      <div className="flex gap-2">
        <Button size="sm" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          Previous
        </Button>
        <Button size="sm" disabled={to >= total} onClick={() => onChange(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}
